/**
 * 编辑器修改源
 */
export enum ChangeSource {
    user = "user",
    api = "api",
    slient = "slient",
}

export type Range = { index: Number; length: Number };

export type Delta = Array<object>;

export enum EventName {
    textChange = "text-change",
    selectionChange = "selection-change",
    editorChange = "editor-change",
}

/**
 * 文本改变事件监听回调函数
 */
export type TextChangeHandler = (
    delta: Delta,
    oldDelta: Delta,
    source: ChangeSource
) => void;
/**
 * 编辑器光标选择事件监听回调
 */
export type SelectionChangeHandler = (
    range: Range,
    oldRange: Range,
    source: ChangeSource
) => void;
/**
 * 编辑器事件监听回调
 */
export type EditorChangeHandler =
    | ((
          eventName: EventName.textChange,
          delta: Delta,
          oldDelta: Delta,
          source: ChangeSource
      ) => void)
    | ((
          eventName: EventName.selectionChange,
          range: Range,
          oldRange: Range,
          source: ChangeSource
      ) => void);

export type QuillEditor = {
    /**
     * 剪切板模块
     */
    clipboard: any;

    /**
     * 编辑器内容HTML根节点
     */
    root: HTMLElement;
    /**
     * 监听文本修改事件
     */
    on(
        eventName: EventName.textChange,
        handler: TextChangeHandler
    ): QuillEditor;
    /**
     * 监听光标选择事件
     */
    on(
        eventName: EventName.selectionChange,
        handler: SelectionChangeHandler
    ): QuillEditor;
    /**
     * 监听编辑器事件
     */
    on(
        eventName: EventName.editorChange,
        handler: EditorChangeHandler
    ): QuillEditor;
    /**
     * 一次性监听文本修改事件
     */
    once(
        eventName: EventName.textChange,
        handler: TextChangeHandler
    ): QuillEditor;
    /**
     * 一次性监听光标选择事件
     */
    once(
        eventName: EventName.selectionChange,
        handler: SelectionChangeHandler
    ): QuillEditor;
    /**
     * 一次性监听编辑器事件
     */
    once(
        eventName: EventName.editorChange,
        handler: EditorChangeHandler
    ): QuillEditor;
    /**
     * 移除文本修改监听事件
     */
    off(
        eventName: EventName.textChange,
        handler: TextChangeHandler
    ): QuillEditor;
    /**
     * 移除光标选择监听事件
     */
    off(
        eventName: EventName.selectionChange,
        handler: SelectionChangeHandler
    ): QuillEditor;
    /**
     * 移除编辑器监听事件
     */
    off(
        eventName: EventName.editorChange,
        handler: EditorChangeHandler
    ): QuillEditor;
    /**
     * 设置编辑器html文本
     * @param html 要设置的html文本
     */
    pasteHTML(html: string): void;
    /**
     * 设置编辑器纯文本
     * @param text 要设置的纯文本
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     * @returns 代表本次改变的{@link Delta}对象
     */
    setText(text: string, source?: ChangeSource): Delta;
    /**
     * 获取编辑器光标选择范围
     * @param focus 是否先让编辑器聚焦
     * @returns 返回编辑器光标选择范围，编辑器没有聚焦时会返回null
     */
    getSelection(focus?: boolean): Range;
    /**
     * 设置编辑器光标选择范围
     * @param index 选择范围开始位置
     * @param length 选择范围的长度，默认为0，为0时指把光标跳转到对应位置
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    setSelection(index: number, length?: number, source?: ChangeSource): void;
    /**
     * 设置编辑器光标选择范围
     * @param range 选择范围
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    setSelection(range: Range, source?: ChangeSource): void;
    /**
     * 获取编辑器的纯文本内容
     * @param index 获取的纯文本开始位置，默认0
     * @param length 获取的纯文本长度，默认剩余长度
     */
    getText(index?: number, length?: number): string;
    /**
     * 移除指定文本格式
     * @param index 要移除格式文本的开始位置
     * @param length 要移除格式文本的长度
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    formatText(index: number, length: number, source?: ChangeSource): Delta;
    /**
     * @param index 要格式文本的开始位置
     * @param length 要格式文本的长度
     * @param format 指定的格式名，详情看{@link https://quilljs.com/docs/formats}
     * @param value 指定的格式值，为false时表示移除对应的格式，详情看{@link https://quilljs.com/docs/formats}
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    formatText(
        index: number,
        length: number,
        format: string,
        value: string,
        source?: ChangeSource
    ): Delta;
    /**
     * @param index 要格式文本的开始位置
     * @param length 要格式文本的长度
     * @param formats 指定的格式，对应值为false时表示移除对应格式，详情看{@link https://quilljs.com/docs/formats}
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    formatText(
        index: number,
        length: number,
        formats: object,
        source?: string
    ): Delta;
    /**
     * 删除指定文本
     * @param index 要删除文本的开始位置
     * @param length 要删除文本的长度
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    deleteText(index: number, length: number, source?: ChangeSource): Delta;
    /**
     * 插入指定文本到指定位置
     * @param index 要插入的位置
     * @param text 要插入的文本
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    insertText(index: number, text: string, source?: ChangeSource): Delta;
    /**
     * 插入指定格式的文本到指定位置
     * @param index 要插入的位置
     * @param text 要插入的文本
     * @param format 插入文本的格式名，详情看{@link https://quilljs.com/docs/formats}
     * @param value 插入文本的格式值，为false时表示移除对应的格式，详情看{@link https://quilljs.com/docs/formats}
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    insertText(
        index: Number,
        text: String,
        format: String,
        value: any,
        source?: ChangeSource
    ): Delta;
    /**
     * 插入指定格式的文本到指定位置
     * @param index 要插入的位置
     * @param text 要插入的文本
     * @param formats 插入文本的格式，对应值为false时表示移除对应格式，详情看{@link https://quilljs.com/docs/formats}
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    insertText(
        index: Number,
        text: String,
        formats: object,
        source?: ChangeSource
    ): Delta;
    /**
     * 申请{@link delta}改变到编辑器内容中
     * @param delta 要申请的delta改变
     * @param source 指定这次操作来源，默认{@link ChangeSource.api}
     */
    updateContents(delta: Delta, source?: ChangeSource): Delta;
};
